import { AuthContext } from '@modules/auth/contexts/auth-plus.context';
import { useContext, useEffect, useState } from 'react';
import { FaCopy, FaSyncAlt, FaCog, FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import { SystemSettingService } from '../services/system-setting.service';

function OpenAISetting() {
  const { auth, setAuth, logout } = useContext(AuthContext);

  const [assistantId, setAssistantId] = useState('');
  const [vectorStorageId, setVectorStorageId] = useState('');
  const [aiStatus, setAiStatus] = useState('down');
  const [showUpdateCard, setShowUpdateCard] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    instruction: '',
    model: '',
    vectorStorageId: ''
  });

  useEffect(() => {
    fetchInitialData();
  }, [auth]);

  const fetchInitialData = async () => {
    await Promise.all([
      fetchAIStatus(),
      fetchAssistantId(),
      fetchVectorStorageId()
    ]);
  };

  const fetchAIStatus = async () => {
    if (!auth) return;
    try {
      const res = await SystemSettingService.getAIAssistantStatus(auth, setAuth, logout);
      setAiStatus(res.data === true ? 'live' : 'down');
    } catch (err: any) {
      console.error('Failed to fetch AI status:', err);
    }
  };

  const fetchAssistantId = async () => {
    if (!auth) return;
    try {
      const res = await SystemSettingService.getAIAssistantId(auth, setAuth, logout);
      setAssistantId(res.data || '');
    } catch (err: any) {
      console.error('Failed to fetch Assistant ID:', err);
    }
  };

  const fetchVectorStorageId = async () => {
    if (!auth) return;
    try {
      const res = await SystemSettingService.getVectorStorageId(auth, setAuth, logout);
      setVectorStorageId(res.data || '');
    } catch (err: any) {
      console.error('Failed to fetch Vector Storage ID:', err);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    if (!text) {
      alert('No data to copy!');
      return;
    }
    navigator.clipboard.writeText(text);
    alert(`${label} copied to clipboard!`);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isSubmitEnabled = () => {
    const trimmed = {
      instruction: formData.instruction.trim(),
      model: formData.model.trim(),
      vectorStorageId: formData.vectorStorageId.trim()
    };
    return trimmed.instruction || trimmed.model || trimmed.vectorStorageId;
  };

  const handleSubmit = async () => {
    if (!auth) return;

    setLoading(true);
    setError(null);

    try {
      await SystemSettingService.updateAIAssistant(
        auth,
        setAuth,
        logout,
        formData.instruction,
        formData.model,
        formData.vectorStorageId
      );

      alert('AI Assistant updated successfully!');
      setShowUpdateCard(false);
      setFormData({ instruction: '', model: '', vectorStorageId: '' });

      // Refresh data
      await fetchInitialData();
    } catch (err: any) {
      setError(err.message || 'Failed to update AI Assistant');
      alert('Failed to update AI Assistant. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateVectorStorage = async () => {
    const newId = prompt('Enter new Vector Storage ID:');
    if (!newId || !newId.trim()) return;

    if (!auth) return;

    setLoading(true);
    setError(null);

    try {
      await SystemSettingService.updateVectorStorage(auth, setAuth, logout, newId.trim());
      setVectorStorageId(newId.trim());
      alert('Vector Storage ID updated successfully!');

      // Refresh data
      await fetchVectorStorageId();
    } catch (err: any) {
      setError(err.message || 'Failed to update Vector Storage ID');
      alert('Failed to update Vector Storage ID. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInitialize = async () => {
    if (!auth) return;

    if (!window.confirm('Are you sure you want to initialize the AI Assistant? This will set up a new assistant.')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await SystemSettingService.initialAIAssistant(auth, setAuth, logout);

      if (res.data === true) {
        alert('AI Assistant initialized successfully!');
        // Refresh all data
        await fetchInitialData();
      } else {
        throw new Error('Initialization failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to initialize AI Assistant');
      alert('Failed to initialize AI Assistant. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <h1 className="display-4 fw-bold text-dark mb-4">AI Assistant</h1>

            {/* Error Alert */}
            {error && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                <strong>Error:</strong> {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError(null)}
                  aria-label="Close"
                ></button>
              </div>
            )}

            {/* Loading Overlay */}
            {loading && (
              <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
                <div className="text-center">
                  <FaSpinner className="text-white fa-spin" size={50} />
                  <p className="text-white mt-3 fs-5">Loading...</p>
                </div>
              </div>
            )}

            {/* Initialize Section */}
            <div className="card shadow-sm mb-4 border-start border-primary border-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="card-title fw-bold mb-1">Initialize AI Assistant</h5>
                    <p className="text-muted small mb-0">Set up your AI assistant for the first time</p>
                  </div>
                  <button
                    onClick={handleInitialize}
                    className="btn btn-primary btn-lg shadow-sm"
                    disabled={loading || aiStatus === 'live'}
                    title={aiStatus === 'live' ? 'Assistant already initialized. For more information, contact developer.' : ''}
                    style={{ cursor: aiStatus === 'live' ? 'not-allowed' : 'pointer' }}
                  >
                    {loading ? <FaSpinner className="fa-spin me-2" /> : null}
                    Initialize AI Assistant
                  </button>
                </div>
              </div>
            </div>

            {/* AI Status */}
            <div className="card shadow-sm mb-4">
              <div className="card-body">
                <div className="d-flex align-items-center gap-3">
                  <span className="fw-semibold">AI Status:</span>
                  <div className="d-flex align-items-center gap-2">
                    {aiStatus === 'live' ? (
                      <>
                        <FaCheckCircle className="text-success" size={20} />
                        <span className="text-success fw-medium">Live</span>
                      </>
                    ) : (
                      <>
                        <FaTimesCircle className="text-danger" size={20} />
                        <span className="text-danger fw-medium">Down</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Assistant ID Section */}
            <div className="card shadow-sm mb-4">
              <div className="card-body">
                <label className="form-label fw-semibold text-secondary small">Assistant ID</label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control bg-light font-monospace"
                    value={assistantId || 'Not initialized yet'}
                    readOnly
                  />
                  <button
                    onClick={() => copyToClipboard(assistantId, 'Assistant ID')}
                    className="btn btn-outline-secondary"
                    title="Copy to clipboard"
                    disabled={!assistantId}
                  >
                    <FaCopy />
                  </button>
                </div>
              </div>
            </div>

            {/* Vector Storage ID Section */}
            <div className="card shadow-sm mb-4">
              <div className="card-body">
                <label className="form-label fw-semibold text-secondary small">Vector Storage ID</label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control bg-light font-monospace"
                    value={vectorStorageId || 'Not set yet'}
                    readOnly
                  />
                  <button
                    onClick={() => copyToClipboard(vectorStorageId, 'Vector Storage ID')}
                    className="btn btn-outline-secondary"
                    title="Copy to clipboard"
                    disabled={!vectorStorageId}
                  >
                    <FaCopy />
                  </button>
                  <button
                    onClick={handleUpdateVectorStorage}
                    className="btn btn-dark"
                    title="Update Vector Storage ID"
                    disabled={loading}
                  >
                    <FaSyncAlt />
                  </button>
                </div>
              </div>
            </div>

            {/* Update AI Assistant Section */}
            <div className="card shadow-sm mb-4">
              <div className="card-body p-0">
                <button
                  onClick={() => setShowUpdateCard(!showUpdateCard)}
                  className="btn btn-gradient w-100 d-flex justify-content-between align-items-center p-4 text-start"
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                    borderRadius: '0.375rem'
                  }}
                  disabled={loading}
                >
                  <span className="text-white fw-semibold d-flex align-items-center gap-2">
                    <FaCog />
                    Update AI Assistant Configuration
                  </span>
                  <span className="text-white fs-3">{showUpdateCard ? '−' : '+'}</span>
                </button>

                {showUpdateCard && (
                  <div className="p-4 bg-light">
                    {/* Instruction Field */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Instruction <span className="text-muted fw-normal">(optional)</span>
                      </label>
                      <textarea
                        value={formData.instruction}
                        onChange={(e) => handleInputChange('instruction', e.target.value)}
                        className="form-control"
                        rows={8}
                        placeholder="Enter detailed instructions for your AI assistant..."
                        disabled={loading}
                      />
                    </div>

                    {/* Model Field */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Model <span className="text-muted fw-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        value={formData.model}
                        onChange={(e) => handleInputChange('model', e.target.value)}
                        className="form-control"
                        placeholder="e.g., gpt-4-turbo"
                        disabled={loading}
                      />
                      <div className="form-text">
                        P/S: Check available models at{' '}
                        <a
                          href="https://platform.openai.com/docs/models"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          https://platform.openai.com/docs/models
                        </a>
                      </div>
                    </div>

                    {/* Vector Storage ID Field */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Vector Storage ID <span className="text-muted fw-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        value={formData.vectorStorageId}
                        onChange={(e) => handleInputChange('vectorStorageId', e.target.value)}
                        className="form-control"
                        placeholder="vs_..."
                        disabled={loading}
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      onClick={handleSubmit}
                      disabled={!isSubmitEnabled() || loading}
                      className={`btn w-100 fw-semibold ${isSubmitEnabled() && !loading
                        ? 'btn-success'
                        : 'btn-secondary'
                        }`}
                    >
                      {loading ? (
                        <>
                          <FaSpinner className="fa-spin me-2" />
                          Updating...
                        </>
                      ) : (
                        'Submit Changes'
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default OpenAISetting;
